import { computed, Injectable, signal, untracked } from '@angular/core';

import { SnackbarType } from '@shared';
import { take } from 'rxjs/operators';
import { Subscription, timer } from 'rxjs';

export interface SnackbarOptions {
  /** When true, the banner does not auto-dismiss after 3s — it stays until `dismiss()` is called. */
  sticky?: boolean;
  /** When true, the banner renders a close (✕) button so the user can dismiss it manually. */
  dismissible?: boolean;
}

export interface SnackbarState extends SnackbarOptions {
  type: SnackbarType;
  title: string;
  subTitle: string;
}

/** One entry of the banner stack. `id` is the handle a caller dismisses later. */
export interface SnackbarBanner extends SnackbarState {
  id: number;
}

/**
 * RID-1355 — the top banners are a stack, not a single slot.
 *
 * Each banner keeps its own auto-dismiss timer, so a short-lived one expiring never takes a sticky
 * neighbour with it, and a new banner never overwrites the one already on screen: they render one
 * under another (`SnackbarComponent`). Banners repeating the exact same copy are refreshed rather
 * than duplicated — several callers announce from an `effect` that re-runs on unrelated changes.
 */
@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  readonly banners = signal<SnackbarBanner[]>([]);
  readonly bannerShown = computed(() => this.banners().length > 0);

  private nextId = 1;
  private readonly autoDismiss = new Map<number, Subscription>();

  error(title: string, subTitle: string, options?: SnackbarOptions): number {
    return this.show(SnackbarType.ERROR, title, subTitle, options);
  }

  alert(title: string, subTitle: string, options?: SnackbarOptions): number {
    return this.show(SnackbarType.ALERT, title, subTitle, options);
  }

  info(title: string, subTitle: string, options?: SnackbarOptions): number {
    return this.show(SnackbarType.INFO, title, subTitle, options);
  }

  infoSticky(title: string, subTitle?: string, options?: SnackbarOptions): number {
    return this.show(SnackbarType.INFO, title, subTitle ?? '', { ...options, sticky: true });
  }

  /** Drops one banner by `id`, or the whole stack when called without one. */
  dismiss(id?: number): void {
    if (id === undefined) {
      this.autoDismiss.forEach((subscription) => subscription.unsubscribe());
      this.autoDismiss.clear();
      this.banners.set([]);
      return;
    }

    this.clearTimer(id);
    this.banners.update((banners) => banners.filter((banner) => banner.id !== id));
  }

  private show(type: SnackbarType, title: string, subTitle: string, options: SnackbarOptions = {}): number {
    // Untracked: callers announce from an `effect`, and tracking the stack would re-run that effect
    // on every dismiss and bring a just-closed banner straight back.
    const existing = untracked(() => this.banners()).find(
      (banner) => banner.type === type && banner.title === title && banner.subTitle === subTitle,
    );

    // Same copy already on screen — restart its life instead of stacking a duplicate.
    if (existing) {
      this.scheduleAutoDismiss(existing.id, options.sticky);
      return existing.id;
    }

    const id = this.nextId++;
    this.banners.update((banners) => [...banners, { id, type, title, subTitle, ...options }]);
    this.scheduleAutoDismiss(id, options.sticky);

    return id;
  }

  private scheduleAutoDismiss(id: number, sticky?: boolean): void {
    this.clearTimer(id);
    if (sticky) return;

    this.autoDismiss.set(
      id,
      timer(3000)
        .pipe(take(1))
        .subscribe(() => this.dismiss(id)),
    );
  }

  private clearTimer(id: number): void {
    this.autoDismiss.get(id)?.unsubscribe();
    this.autoDismiss.delete(id);
  }
}
